import { useEffect, useState } from 'react';
import { Sheet } from 'react-modal-sheet';
import { t } from 'i18next';
import { useColorMode } from '@chakra-ui/react';
import { Button, ButtonToggle, RoundBadge } from '@peacecover/component';
import useUserQuery from '../../../presentation/hooks/queries/useUserQuery';
import { useGetBettieProducts } from '../../../domains/bettie/ui/hooks/useGetBettieProducts';
import { openNewTab } from '../../../utils/UIUtils';
import { getOneTimePaymentLaunchPath, getPgProvider } from '../../../utils/paymentProvider';
import PayLetterPaymentMethodSelector from '../../../presentation/payment/components/PayLetterPaymentMethodSelector';
import BettieAgreementSection from '../../../presentation/chat/components/BettieAgreementSection';
import { BettieGradientColor } from '../../../constants/BettieGradientColor';
import type { PayLetterPaymentMethod } from '../../../domains/orders/services/orderServices';

interface BettieCreateRoomTicketBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const BettieCreateRoomTicketBottomSheet = ({
  isOpen,
  onClose
}: BettieCreateRoomTicketBottomSheetProps) => {
  const { colorMode } = useColorMode();
  const { data: roomTicketProducts } = useGetBettieProducts({
    assetType: 'bettie_chat_room_ticket'
  });

  const { data: user, refetch: refetchUser } = useUserQuery();

  const [isPayLetterMethodSelectorOpen, setIsPayLetterMethodSelectorOpen] = useState(false);

  useEffect(() => {
    refetchUser();
    const onFocus = () => {
      refetchUser();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, [refetchUser]);

  if (!roomTicketProducts?.length || !user) return null;

  // 방 생성권 아이템
  const productItem = roomTicketProducts[0];

  const handleClick = (paymentMethod?: PayLetterPaymentMethod) => {
    const amount = Number(productItem.baseAmount - productItem.discountAmount);
    const oneTimePaymentLaunchPath = getOneTimePaymentLaunchPath();
    const isPayLetter = getPgProvider() === 'pay-letter';

    if (user?.identified && isPayLetter && !paymentMethod) {
      setIsPayLetterMethodSelectorOpen(true);
      return;
    }

    if (user?.identified) {
      const encodedProductName = encodeURIComponent(productItem.name);
      openNewTab(
        `${oneTimePaymentLaunchPath}?amount=${amount}&productName=${encodedProductName}&productId=${productItem.bettieProductId}${isPayLetter ? `&paymentMethod=${paymentMethod}` : ''}`
      );
    } else {
      const encodedProductName = encodeURIComponent(productItem.name);
      openNewTab(
        `/identity-verification/intro?amount=${amount}&productName=${encodedProductName}&productId=${productItem.bettieProductId}`
      );
    }
    onClose();
  };

  const displayAmount = (amount: string) => {
    switch (productItem.currency) {
      case 'KRW':
        return `${amount}원`;
      case 'USD':
        return `${amount}$`;
      default:
        return `${amount}원`;
    }
  };

  const isDiscounted = Number(productItem.discountAmount) > 0;

  return (
    <Sheet isOpen={isOpen} onClose={onClose} detent="content">
      <Sheet.Container className="!rounded-t-[20px] !bg-background-light dark:!bg-background-elevated">
        <Sheet.Header className="pt-[32px] px-[24px] flex items-center justify-between">
          <p className="text-[20px] font-bold leading-[24px] text-text-main-lv2">
            {t('BettieChargeBottomSheet.roomTicketTitle')}
          </p>
          <p className="text-[12px] font-medium leading-[14px] text-text-sub-lv1">
            {t('BettieChargeBottomSheet.cannotUseBcCard')}
          </p>
        </Sheet.Header>
        <div className="px-[24px] pt-[20px] pb-[18px] flex flex-col gap-[30px]">
          <div
            className="p-[1px] rounded-[18px]"
            style={{
              background:
                BettieGradientColor.bettieProductOutline[colorMode as 'light' | 'dark'].background
            }}
          >
            <div
              className="rounded-[17px] z-10"
              style={{
                background:
                  BettieGradientColor.bettieProductFill[colorMode as 'light' | 'dark'].background
              }}
            >
              <div className="flex items-center justify-between p-[20px]">
                <div className="flex items-center gap-[6px]">
                  <ButtonToggle type="radio" size="lg" checked={true} />
                  <div className="flex items-center gap-[4px]">
                    <p className="text-[16px] font-semibold leading-[19px] text-text-main-lv2">
                      {productItem.name}
                    </p>
                    {isDiscounted && <RoundBadge colorVariant="secondary">SALE</RoundBadge>}
                  </div>
                </div>
                {isDiscounted ? (
                  <div className="flex flex-col gap-[2px] items-end">
                    <p className="text-[15px] font-semibold bg-gradient-single-red bg-clip-text text-transparent flex items-center">
                      <span className="mr-[5px] text-[8px]">{productItem.currency}</span>
                      {displayAmount(
                        Number(productItem.baseAmount - productItem.discountAmount).toLocaleString()
                      )}
                    </p>
                    <p className="text-[12px] font-medium leading-[14px] text-text-sub-lv1 line-through">
                      {displayAmount(
                        Number(productItem.baseAmount - productItem.discountAmount).toLocaleString()
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-[15px] font-semibold text-text-primary flex items-center">
                    <span className="mr-[5px] text-[8px]">{productItem.currency}</span>
                    {displayAmount(
                      Number(productItem.baseAmount - productItem.discountAmount).toLocaleString()
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>
          <BettieAgreementSection />
        </div>
        <Button
          size="large"
          className="mx-[24px] rounded-[18px] text-[17px] font-semibold leading-[20px]"
          onClick={() => handleClick()}
        >
          {t('BettieChargeBottomSheet.agreeAndPay', {
            price: `${Number(productItem.baseAmount - productItem.discountAmount).toLocaleString()}원`
          })}
        </Button>
      </Sheet.Container>
      <Sheet.Backdrop onClick={onClose} className="!bg-[rgba(0,0,0,0.50)]" />
      <PayLetterPaymentMethodSelector
        isOpen={isPayLetterMethodSelectorOpen}
        onClose={() => setIsPayLetterMethodSelectorOpen(false)}
        onSelect={(paymentMethod) => {
          setIsPayLetterMethodSelectorOpen(false);
          handleClick(paymentMethod);
        }}
      />
    </Sheet>
  );
};

export default BettieCreateRoomTicketBottomSheet;
